import React, { useState } from 'react';
import {
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  FileArchive,
  File,
  Download,
  ExternalLink
} from 'lucide-react';
import api from 'services/api';
import VisualizadorPDF from './VisualizadorPDF';

const ArquivoItem = ({ arquivo, contaId: _contaId }) => {
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [arquivoBase64, setArquivoBase64] = useState(null);

  const visualizarArquivo = async () => {
    if (arquivoBase64) return;
    setLoading(true);
    setErro(null);
    try {
      const response = await api.post('/n8n/administracao/a_receber/arquivo', {
        id_arquivo: arquivo.id
      });
      if (response.data.file_base64) {
        const base64 = response.data.file_base64;
        if (base64.length < 100) {
          setErro('Arquivo base64 muito pequeno, pode estar corrompido');
          setLoading(false);
          return;
        }
        
        if (arquivo.extensao?.toLowerCase() === 'pdf') {
          try {
            const decoded = atob(base64);
            if (decoded.length < 100) {
              setErro('Arquivo PDF muito pequeno, pode estar corrompido');
              setLoading(false);
              return;
            }
            if (!decoded.startsWith('%PDF') && decoded.length < 5000) {
              console.warn('PDF pode não começar com %PDF, mas tentando carregar mesmo assim');
            }
          } catch (e) {
            console.error('Erro ao decodificar base64 do PDF:', e);
          }
        }
        
        setArquivoBase64(base64);
      } else {
        setErro('Arquivo não encontrado na resposta do servidor');
      }
    } catch (error) {
      console.error('Erro ao buscar arquivo:', error);
      setErro(error.response?.data?.message || 'Erro ao carregar arquivo. Verifique sua conexão e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getIconeArquivo = (extensao) => {
    const ext = extensao?.toLowerCase();
    if (ext === 'pdf') return <FileText className="h-8 w-8 text-red-500" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return <ImageIcon className="h-8 w-8 text-blue-500" />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="h-8 w-8 text-green-500" />;
    if (['zip', 'rar', '7z'].includes(ext)) return <FileArchive className="h-8 w-8 text-yellow-600" />;
    return <File className="h-8 w-8 text-[color:var(--text-muted)]" />;
  };

  const getTipoMIME = (extensao) => {
    const ext = extensao?.toLowerCase();
    const tiposMIME = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      bmp: 'image/bmp',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      csv: 'text/csv',
      zip: 'application/zip',
      rar: 'application/x-rar-compressed',
      '7z': 'application/x-7z-compressed'
    };
    return tiposMIME[ext] || 'application/octet-stream';
  };

  const formatarData = (data) => {
    if (!data) return 'Data não informada';
    if (typeof data === 'string' && data.includes('/')) return data;
    try {
      const dataObj = new Date(data);
      if (isNaN(dataObj.getTime())) return 'Data inválida';
      return dataObj.toLocaleDateString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  const renderizarArquivo = () => {
    if (!arquivoBase64) return null;
    
    const tipoMIME = getTipoMIME(arquivo.extensao);
    const dataURL = `data:${tipoMIME};base64,${arquivoBase64}`;
    
    if (tipoMIME.startsWith('image/')) {
      return (
        <div className="text-center w-full max-w-full overflow-x-hidden">
          <img 
            src={dataURL} 
            alt={arquivo.nome} 
            className="max-w-full max-h-96 mx-auto rounded-lg shadow-lg"
            style={{ maxWidth: '100%', height: 'auto' }}
            onError={(e) => {
              console.error('Erro ao carregar imagem:', e);
              e.target.style.display = 'none';
            }}
          />
        </div>
      );
    }
    
    if (tipoMIME === 'application/pdf') {
      return (
        <VisualizadorPDF 
          dataURL={dataURL} 
          nomeArquivo={arquivo.nome} 
        />
      );
    }
    
    return (
      <div className="text-center p-8">
        <FileText className="h-16 w-16 mx-auto text-[color:var(--text-muted)] mb-4" />
        <p className="text-[color:var(--text-muted)] mb-4">Arquivo não pode ser visualizado diretamente</p>
        <a href={dataURL} download={arquivo.nome} className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          <Download className="h-4 w-4 mr-2" />
          Baixar Arquivo
        </a>
      </div>
    );
  };

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
            <div className="flex-shrink-0">{getIconeArquivo(arquivo.extensao)}</div>
            <div className="flex-1 min-w-0">
              <h5 className="text-sm font-medium text-[color:var(--text-primary)] break-words">{arquivo.descricao || arquivo.nome || 'Arquivo sem nome'}</h5>
              <div className="mt-1 space-y-1">
                <p className="text-xs text-[color:var(--text-muted)] break-words">
                  <span className="font-medium">Tipo:</span> {arquivo.extensao?.toUpperCase() || 'Não informado'}
                </p>
                {arquivo.nome && arquivo.nome !== arquivo.descricao && (
                  <p className="text-xs text-[color:var(--text-muted)] break-words">
                    <span className="font-medium">Nome original:</span> {arquivo.nome}
                  </p>
                )}
                {arquivo.data_envio && (
                  <p className="text-xs text-[color:var(--text-muted)]">
                    <span className="font-medium">Enviado em:</span> {formatarData(arquivo.data_envio)}
                  </p>
                )}
                <p className="text-xs text-[color:var(--text-muted)]">
                  <span className="font-medium">ID:</span> {arquivo.id}
                </p>
              </div>
            </div>
          </div>
          <button 
            onClick={visualizarArquivo} 
            disabled={loading} 
            className="flex-shrink-0 flex items-center px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="hidden sm:inline">Carregando...</span>
              </>
            ) : (
              <>
                <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="hidden sm:inline">Visualizar</span>
              </>
            )}
          </button>
        </div>
      </div>

      {erro && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600 break-words">{erro}</p>
        </div>
      )}

      {arquivoBase64 && (
        <div className="mt-4 p-3 sm:p-4 bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-md w-full max-w-full overflow-x-hidden">
          {renderizarArquivo()}
        </div>
      )}
    </div>
  );
};

export default ArquivoItem;
